import { useState, useCallback } from 'react';
import { awardXP, getUserBadges } from '../lib/civicLevelService';
import type { XPAwardResult, Badge } from '../lib/civicLevelTypes';

interface UseCivicXPReturn {
  awardXPForAction: (actionId: string) => Promise<XPAwardResult | null>;
  showLevelUpModal: boolean;
  levelUpData: { newLevel: string; badge: Badge | null } | null;
  closeLevelUpModal: () => void;
  isAwarding: boolean;
}

export function useCivicXP(userId: string): UseCivicXPReturn {
  const [showLevelUpModal, setShowLevelUpModal] = useState(false);
  const [levelUpData, setLevelUpData] = useState<{ newLevel: string; badge: Badge | null } | null>(null);
  const [isAwarding, setIsAwarding] = useState(false);

  const awardXPForAction = useCallback(
    async (actionId: string) => {
      if (!userId || isAwarding) return null;

      setIsAwarding(true);
      try {
        const result = await awardXP(userId, actionId);

        if (result && result.leveled_up) {
          const badges = await getUserBadges(userId);
          const newBadge = badges.find(b => b.level_name === result.new_level);

          setLevelUpData({
            newLevel: result.new_level,
            badge: newBadge || null,
          });
          setShowLevelUpModal(true);
        }

        return result;
      } catch (error) {
        console.error('Error awarding XP:', error);
        return null;
      } finally {
        setIsAwarding(false);
      }
    },
    [userId, isAwarding]
  );

  const closeLevelUpModal = useCallback(() => {
    setShowLevelUpModal(false);
    setLevelUpData(null);
  }, []);

  return {
    awardXPForAction,
    showLevelUpModal,
    levelUpData,
    closeLevelUpModal,
    isAwarding,
  };
}
